import _ from "lodash";

// Etiquetas, horarios y enfriamientos de las reglas («Avisar o actuar cuando pase algo»). Movidos tal cual desde la
// antigua DeviceAlertRulesIndex: los usan el editor, la tarjeta de la lista y su detalle.
export const UNGROUPED_GROUP_LABEL = "Sin agrupar";

export const OPERATOR_LABELS = {
    eq: "Igual (=)",
    neq: "Distinto (!=)",
    gt: "Mayor que (>)",
    gte: "Mayor o igual (>=)",
    lt: "Menor que (<)",
    lte: "Menor o igual (<=)",
};

export const OPERATOR_SYMBOLS = {
    eq: "=",
    neq: "!=",
    gt: ">",
    gte: ">=",
    lt: "<",
    lte: "<=",
};

export const CONNECTOR_LABELS = {
    all: "Todas (AND)",
    any: "Cualquiera (OR)",
};

export const TRIGGER_CONNECTOR_LABELS = {
    all: "Todos (AND)",
    any: "Cualquiera (OR)",
};

// Numeración de Date#getDay y Time#wday en Ruby: 0 = domingo.
export const WEEK_DAY_OPTIONS = [
    { value: 1, label: "L" },
    { value: 2, label: "M" },
    { value: 3, label: "M" },
    { value: 4, label: "J" },
    { value: 5, label: "V" },
    { value: 6, label: "S" },
    { value: 0, label: "D" },
];

export const DEFAULT_SCHEDULE_START_MINUTE = 9 * 60;
export const DEFAULT_SCHEDULE_END_MINUTE = 18 * 60;

export const minutesToTimeValue = (minutes) => {
    const total = Number(minutes);
    if (!Number.isFinite(total)) return "";
    return `${Math.floor(total / 60)}`.padStart(2, "0") + ":" + `${total % 60}`.padStart(2, "0");
};

// Etiqueta para las tarjetas de la lista; vacía cuando la regla notifica siempre.
export const describeSchedule = (rule) => {
    const preset = detectSchedulePreset(rule);
    if (preset === "always") return "";
    if (preset !== "custom") return _.find(SCHEDULE_PRESETS, { key: preset })?.label || "";

    const window = hasScheduleWindow(rule)
        ? `${minutesToTimeValue(rule.schedule_start_minute)} a ${minutesToTimeValue(rule.schedule_end_minute)}`
        : "24 h";
    const days = _.compact(_.map(_.sortBy(rule?.schedule_days || []), (day) => _.find(WEEK_DAY_OPTIONS, { value: day })?.label));
    return _.compact([window, days.length && days.length < 7 ? days.join("") : ""]).join(" · ");
};

export const hasScheduleWindow = (rule) => !_.isNil(rule?.schedule_start_minute) && !_.isNil(rule?.schedule_end_minute);

export const minutesToDate = (minutes) => {
    if (_.isNil(minutes)) return null;
    const total = Number(minutes);
    if (!Number.isFinite(total)) return null;
    return new Date(2000, 0, 1, Math.floor(total / 60), total % 60);
};

export const dateToMinutes = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    return (date.getHours() * 60) + date.getMinutes();
};

export const WEEKDAY_VALUES = [1, 2, 3, 4, 5];
export const WEEKEND_VALUES = [0, 6];
export const ALL_DAY_VALUES = [0, 1, 2, 3, 4, 5, 6];

// Opciones frecuentes; "custom" deja elegir días y horas a mano.
export const SCHEDULE_PRESETS = [
    { key: "always", label: "Todos los días, 24 h", start: null, end: null, days: [] },
    { key: "business", label: "Días de semana, 09:00 a 18:00", start: 9 * 60, end: 18 * 60, days: WEEKDAY_VALUES },
    { key: "weekdays", label: "Días de semana, 24 h", start: null, end: null, days: WEEKDAY_VALUES },
    { key: "weekend", label: "Fines de semana, 24 h", start: null, end: null, days: WEEKEND_VALUES },
    { key: "nights", label: "Todas las noches, 21:00 a 06:00", start: 21 * 60, end: 6 * 60, days: [] },
    { key: "custom", label: "Otro…", start: DEFAULT_SCHEDULE_START_MINUTE, end: DEFAULT_SCHEDULE_END_MINUTE, days: ALL_DAY_VALUES },
];

export const detectSchedulePreset = (rule) => {
    const days = _.sortBy(rule?.schedule_days || []);
    const start = _.isNil(rule?.schedule_start_minute) ? null : Number(rule.schedule_start_minute);
    const end = _.isNil(rule?.schedule_end_minute) ? null : Number(rule.schedule_end_minute);
    const preset = _.find(SCHEDULE_PRESETS, (option) => option.key !== "custom"
        && option.start === start && option.end === end && _.isEqual(_.sortBy(option.days), days));
    return preset?.key || "custom";
};

export const CHANNEL_LABELS = {
    email: "Email",
    whatsapp: "WhatsApp",
    slack: "Slack",
};

export const VALUE_KIND_LABELS = {
    numeric: "Numérico",
    text: "Texto",
};

export const COMMAND_TYPE_LABELS = {
    set_state: "Cambiar estado",
};

export const DESIRED_STATE_LABELS = {
    ON: "Encender (ON)",
    OFF: "Apagar (OFF)",
};

export const CONDITION_ROLE_TRIGGER = "trigger";
export const CONDITION_ROLE_FILTER = "filter";

// «Repetir el aviso cada» (cooldown_seconds): mientras siga la alerta, cada cuánto se le repite el aviso a cada
// destinatario. Mínimo 5 minutos (el backend rechaza valores menores); las reglas nuevas parten en 1 hora.
export const MIN_COOLDOWN_SECONDS = 300;
export const DEFAULT_COOLDOWN_SECONDS = 3600;
export const COOLDOWN_OPTIONS = [
    { value: 300, label: "5 min" },
    { value: 900, label: "15 min" },
    { value: 1800, label: "30 min" },
    { value: 3600, label: "1 h" },
    { value: 10800, label: "3 h" },
    { value: 86400, label: "24 h" },
    { value: 172800, label: "2 días" },
    { value: 604800, label: "1 semana" },
];

// «Avisar» de cada destinatario (notify_after_seconds): cuánto tiempo seguido tiene que durar la alerta antes de su
// primer aviso. Se cuenta desde que la regla se empieza a cumplir.
export const NOTIFY_AFTER_OPTIONS = [
    { value: 0, label: "De inmediato" },
    { value: 900, label: "Después de 15 min" },
    { value: 1800, label: "Después de 30 min" },
    { value: 3600, label: "Después de 1 h" },
    { value: 7200, label: "Después de 2 h" },
];
export const MAX_NOTIFY_AFTER_MINUTES = 10080;

// «Avisar cuando vuelva a la normalidad»: minutos seguidos sin cumplirse para darla por resuelta. El mismo margen evita
// que una lectura suelta dentro de rango reinicie los «avisar después de».
export const DEFAULT_RECOVERY_AFTER_SECONDS = 600;
export const RECOVERY_MINUTES_RANGE = { min: 1, max: 1440 };

// «45 min», «1 h 30 min», «2 días».
export const humanDuration = (secondsValue) => {
    const minutes = Math.round(Number(secondsValue || 0) / 60);
    if (minutes < 60) return `${minutes} min`;
    if (minutes % 1440 === 0) return minutes === 1440 ? "1 día" : `${minutes / 1440} días`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

export const notifyAfterLabel = (seconds) => {
    const value = Number(seconds || 0);
    return value > 0 ? `Después de ${humanDuration(value)}` : "De inmediato";
};

export const conditionRole = (condition) => condition?.condition_role || CONDITION_ROLE_TRIGGER;

export const conditionsByRole = (conditions, role) => _.filter(conditions || [], (condition) => conditionRole(condition) === role);

// «Sensor incubación · temperature > 20». Los nombres vienen del serializer; las opciones de edición son el respaldo.
export const conditionSummary = (condition, { devicesById = {}, measurementTypes = [] } = {}) => {
    const fallbackMeasurementName = _.find(measurementTypes, { id: condition?.measurement_type_id })?.name;
    const measurementName = condition?.measurement_name || fallbackMeasurementName || "Métrica";
    const conditionDeviceName = condition?.device_name || devicesById[condition?.device_id]?.name || `Dispositivo ${condition?.device_id}`;
    const threshold = condition?.value_kind === "text" ? condition?.threshold_text : condition?.threshold_numeric;
    return `${conditionDeviceName} · ${measurementName} ${OPERATOR_SYMBOLS[condition?.operator] || condition?.operator} ${threshold}`;
};

export const actionSummary = (action, { actionTargetDevices = [] } = {}) => {
    const targetDeviceName = action?.target_device_name || _.find(actionTargetDevices, { id: action?.target_device_id })?.name || `Dispositivo ${action?.target_device_id}`;
    const commandTypeLabel = COMMAND_TYPE_LABELS[action?.command_type] || action?.command_type;
    const desiredStateLabel = DESIRED_STATE_LABELS[action?.desired_state] || action?.desired_state;
    return `${commandTypeLabel} · ${desiredStateLabel} · ${targetDeviceName}`;
};

export const formatRuleTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return parsedDate.toLocaleString("es-CL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};
